<style>
    .coord-marker {
        display: inline-block;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        margin-right: 8px;
        border: 1px solid rgba(0,0,0,0.2);
    }
    .marker-central { background-color: #FF0000; }
    .marker-nw { background-color: #0000FF; }
    .marker-ne { background-color: #00FF00; }
    .marker-sw { background-color: #800000; }
    .marker-se { background-color: #808080; }
</style>

<div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

    <div class="flex flex-col gap-6">

        <mat-card *ngIf="!reviewMode">
            <mat-card-header>
                <mat-card-title>Find by Address</mat-card-title>
                <mat-card-subtitle>Search for a location on Google to set its coordinates</mat-card-subtitle>
            </mat-card-header>
            <mat-card-content>
                <mat-form-field class="w-full" appearance="outline">
                    <mat-label>Search Google Address</mat-label>
                    <mat-icon matPrefix svgIcon="heroicons_outline:magnifying-glass"></mat-icon>
                    <input
                        matInput
                        placeholder="e.g., 1600 Amphitheatre Parkway, Mountain View, CA"
                        [(ngModel)]="googleAddressSearch"
                        (keydown.enter)="verifyGoogleAddress()">
                    <button mat-icon-button color="primary" matSuffix (click)="verifyGoogleAddress()" matTooltip="Verify Address">
                        <mat-icon svgIcon="heroicons_solid:arrow-right-circle"></mat-icon>
                    </button>
                </mat-form-field>
            </mat-card-content>
        </mat-card>

        <mat-card>
            <mat-card-header>
                <mat-card-title class="flex items-center flex-wrap gap-2">
                    Verified Location
                    <span *ngIf="isMissingData(destination?.ds_googleaddress) || isMissingData(destination?.ds_googlematch)" class="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold animate-pulse shadow-sm">
                        ⚠️ Missing Critical Data
                    </span>
                </mat-card-title>
            </mat-card-header>
            <mat-card-content class="flex flex-col gap-4 pt-4">
                <mat-form-field class="w-full" appearance="outline">
                    <mat-label>
                        Formatted Address
                        <span *ngIf="isMissingData(destination?.ds_googleaddress)" class="text-red-500 font-bold ml-1 animate-pulse">⚠️ Missing</span>
                    </mat-label>
                    <input matInput readonly [value]="destination.ds_googleaddress || ''">
                </mat-form-field>

                <mat-form-field class="w-full" appearance="outline">
                    <mat-label>
                        Google Place Match
                        <span *ngIf="isMissingData(destination?.ds_googlematch)" class="text-red-500 font-bold ml-1 animate-pulse">⚠️ Missing</span>
                    </mat-label>
                    <input matInput readonly [value]="destination.ds_googlematch || ''">
                </mat-form-field>
            </mat-card-content>
            <mat-card-actions class="px-4 pb-4" *ngIf="!reviewMode && !isMissingData(destination?.ds_googleaddress)">
                <button class="w-full" mat-flat-button color="primary" (click)="save.emit()">
                    <mat-icon svgIcon="heroicons_outline:check-circle"></mat-icon>
                    <span>Use This Address and Save</span>
                </button>
            </mat-card-actions>
        </mat-card>

        <mat-accordion class="w-full" multi>
            <mat-expansion-panel [expanded]="true">
                <mat-expansion-panel-header>
                    <mat-panel-title class="font-semibold flex items-center flex-wrap gap-2">
                        <div class="flex items-center">
                            <span class="coord-marker marker-central"></span>
                            Central Point
                        </div>
                        <span *ngIf="isMissingCoordinate(destination?.vr_lat) || isMissingCoordinate(destination?.vr_long)" class="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold animate-pulse shadow-sm">
                            ⚠️ Missing Coordinates
                        </span>
                    </mat-panel-title>
                </mat-expansion-panel-header>
                <div class="grid grid-cols-2 gap-x-4 pt-4">
                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>
                            Latitude
                            <span *ngIf="isMissingCoordinate(destination?.vr_lat)" class="text-red-500 font-bold ml-1 animate-pulse">⚠️</span>
                        </mat-label>
                        <input matInput type="number" [readonly]="reviewMode" [(ngModel)]="destination.vr_lat" (keyup)="onCoordinateKeyup($event, destination.vr_lat, centralMarker, 'vr_lat', true)">
                    </mat-form-field>
                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                        <mat-label>
                            Longitude
                            <span *ngIf="isMissingCoordinate(destination?.vr_long)" class="text-red-500 font-bold ml-1 animate-pulse">⚠️</span>
                        </mat-label>
                        <input matInput type="number" [readonly]="reviewMode" [(ngModel)]="destination.vr_long" (keyup)="onCoordinateKeyup($event, destination.vr_long, centralMarker, 'vr_long', false)">
                    </mat-form-field>
                </div>
            </mat-expansion-panel>

            <mat-expansion-panel *ngIf="destination.bl_locationboundaries">
                <mat-expansion-panel-header>
                    <mat-panel-title class="font-semibold items-center">
                        <mat-icon class="mr-2" svgIcon="heroicons_outline:viewfinder-circle"></mat-icon>
                        Location Boundaries
                    </mat-panel-title>
                </mat-expansion-panel-header>

                <div class="flex flex-col gap-2 pt-4">
                    <div class="flex items-center"><span class="coord-marker marker-nw"></span><span class="font-medium">Northwest</span></div>
                    <div class="grid grid-cols-2 gap-x-4">
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-label>Latitude</mat-label>
                            <input matInput type="number" [readonly]="reviewMode" [(ngModel)]="destination.vr_northwestlat" (keyup)="onCoordinateKeyup($event, destination.vr_northwestlat, firstMarker, 'vr_northwestlat', true)">
                        </mat-form-field>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-label>Longitude</mat-label>
                            <input matInput type="number" [readonly]="reviewMode" [(ngModel)]="destination.vr_northwestlong" (keyup)="onCoordinateKeyup($event, destination.vr_northwestlong, firstMarker, 'vr_northwestlong', false)">
                        </mat-form-field>
                    </div>

                    <mat-divider class="my-2"></mat-divider>

                    <div class="flex items-center"><span class="coord-marker marker-ne"></span><span class="font-medium">Northeast</span></div>
                    <div class="grid grid-cols-2 gap-x-4">
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-label>Latitude</mat-label>
                            <input matInput type="number" [readonly]="reviewMode" [(ngModel)]="destination.vr_northeasttlat" (keyup)="onCoordinateKeyup($event, destination.vr_northeasttlat, secondMarker, 'vr_northeasttlat', true)">
                        </mat-form-field>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-label>Longitude</mat-label>
                            <input matInput type="number" [readonly]="reviewMode" [(ngModel)]="destination.vr_northeastlong" (keyup)="onCoordinateKeyup($event, destination.vr_northeastlong, secondMarker, 'vr_northeastlong', false)">
                        </mat-form-field>
                    </div>

                    <mat-divider class="my-2"></mat-divider>

                    <div class="flex items-center"><span class="coord-marker marker-sw"></span><span class="font-medium">Southwest</span></div>
                    <div class="grid grid-cols-2 gap-x-4">
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-label>Latitude</mat-label>
                            <input matInput type="number" [readonly]="reviewMode" [(ngModel)]="destination.vr_southwestlat" (keyup)="onCoordinateKeyup($event, destination.vr_southwestlat, thirdMarker, 'vr_southwestlat', true)">
                        </mat-form-field>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-label>Longitude</mat-label>
                            <input matInput type="number" [readonly]="reviewMode" [(ngModel)]="destination.vr_southwestlong" (keyup)="onCoordinateKeyup($event, destination.vr_southwestlong, thirdMarker, 'vr_southwestlong', false)">
                        </mat-form-field>
                    </div>

                    <mat-divider class="my-2"></mat-divider>

                    <div class="flex items-center"><span class="coord-marker marker-se"></span><span class="font-medium">Southeast</span></div>
                    <div class="grid grid-cols-2 gap-x-4">
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-label>Latitude</mat-label>
                            <input matInput type="number" [readonly]="reviewMode" [(ngModel)]="destination.vr_southeastlat" (keyup)="onCoordinateKeyup($event, destination.vr_southeastlat, fourthMarker, 'vr_southeastlat', true)">
                        </mat-form-field>
                        <mat-form-field appearance="outline" subscriptSizing="dynamic">
                            <mat-label>Longitude</mat-label>
                            <input matInput type="number" [readonly]="reviewMode" [(ngModel)]="destination.vr_southeastlong" (keyup)="onCoordinateKeyup($event, destination.vr_southeastlong, fourthMarker, 'vr_southeastlong', false)">
                        </mat-form-field>
                    </div>
                </div>
            </mat-expansion-panel>
        </mat-accordion>

    </div>

    <div class="min-h-[400px] lg:min-h-0 w-full">
        <div *ngIf="loadMapVar" class="relative w-full h-full rounded-lg overflow-hidden border">
            <google-map
                #googleMap
                mapId="mapGoogle"
                height="100%"
                width="100%"
                (mapClick)="clickGoogleMap($event)"
                [center]="mapCenter"
                [zoom]="8">
                <map-advanced-marker
                    *ngFor="let marker of markersList"
                    [title]="marker.title"
                    [content]="marker.content"
                    [gmpDraggable]="marker.gmpDraggable"
                    [position]="marker.position"
                    (mapDragend)="onMarkerDragEnd($event, marker)"/>
            </google-map>
        </div>
        <div *ngIf="!loadMapVar" class="flex items-center justify-center w-full h-full rounded-lg border bg-gray-100 dark:bg-gray-800">
            <p class="text-gray-500">Map is loading...</p>
        </div>
    </div>
</div>
